import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "motion/react";
import AnimatedSection from "@/components/AnimatedSection";
import GalleryLightbox from "@/components/GalleryLightbox";
import ResponsiveImage from "@/components/ResponsiveImage";
import { galleryImages } from "@/data/galleryImages";

// 6 curated images across the emotional arc: Aankomst → Ontdekking → Creatie → Verbinding → Stilte
const teaserIndices = [0, 3, 7, 12, 17, 19];
const teaserImages = teaserIndices.map((i) => galleryImages[i]);

const ImageCard = ({
  image,
  className,
  aspectClass,
  delay = 0,
  onClick,
}: {
  image: (typeof galleryImages)[0];
  className?: string;
  aspectClass: string;
  delay?: number;
  onClick: () => void;
}) => (
  <AnimatedSection className={className} delay={delay} gentle>
    <motion.button
      onClick={onClick}
      className="relative w-full h-full rounded-[1.75rem] overflow-hidden group cursor-[zoom-in] block focus:outline-none focus:ring-2 focus:ring-accent/20 focus:ring-offset-4 focus:ring-offset-background will-change-transform"
      whileHover={{
        y: -2,
        boxShadow:
          "0 12px 40px -10px hsl(var(--foreground) / 0.10), 0 2px 12px -2px hsl(var(--foreground) / 0.04)",
      }}
      transition={{ duration: 0.9, ease: [0.22, 0.68, 0.35, 1] }}
      style={{
        boxShadow:
          "0 4px 20px -6px hsl(var(--foreground) / 0.04), 0 1px 3px -1px hsl(var(--foreground) / 0.02)",
      }}
    >
      <ResponsiveImage
        source={image.src}
        alt={image.alt}
        loading="lazy"
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 600px"
        className={`w-full h-full object-cover ${aspectClass} transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,0.68,0.35,1)] group-hover:scale-[1.03]`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/[0.06] via-transparent to-transparent pointer-events-none" />
      <div className="absolute inset-0 rounded-[1.75rem] ring-1 ring-inset ring-foreground/[0.03] pointer-events-none" />
    </motion.button>
  </AnimatedSection>
);

const GalleryTeaser = () => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  return (
    <section className="relative section-padding-lg overflow-hidden">
      <div className="container-narrow">
        {/* Section header */}
        <AnimatedSection className="mb-8 sm:mb-10" gentle>
          <p className="text-[11px] text-muted-foreground/30 font-body tracking-[0.14em] uppercase text-center mb-5">
            Sfeerbeelden
          </p>
          <h2 className="font-heading text-[1.85rem] sm:text-[2rem] lg:text-[2.6rem] text-foreground tracking-tight text-center">
            Een kijkje in onze wereld
          </h2>
        </AnimatedSection>
        <AnimatedSection className="mb-20 sm:mb-28" gentle>
          <p className="text-[14px] text-muted-foreground/40 font-body leading-[2] text-center max-w-[380px] mx-auto">
            Warme momenten van spel, rust, creativiteit en verbinding.
          </p>
        </AnimatedSection>

        {/* Editorial composition — 6 images, 3 asymmetric rows */}
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-5 lg:gap-6">
          {/* Row 1: Hero wide + tall portrait */}
          <ImageCard
            image={teaserImages[0]}
            className="sm:col-span-7"
            aspectClass="aspect-[4/3] sm:aspect-[16/10]"
            onClick={() => setLightboxIndex(teaserIndices[0])}
          />
          <ImageCard
            image={teaserImages[1]}
            className="sm:col-span-5"
            aspectClass="aspect-[4/3] sm:aspect-[4/5]"
            delay={0.08}
            onClick={() => setLightboxIndex(teaserIndices[1])}
          />

          {/* Row 2: Three equal — rhythmic breathing space */}
          <ImageCard
            image={teaserImages[2]}
            className="sm:col-span-4"
            aspectClass="aspect-[4/3] sm:aspect-[1/1]"
            delay={0.12}
            onClick={() => setLightboxIndex(teaserIndices[2])}
          />
          <ImageCard
            image={teaserImages[3]}
            className="sm:col-span-4"
            aspectClass="aspect-[4/3] sm:aspect-[1/1]"
            delay={0.18}
            onClick={() => setLightboxIndex(teaserIndices[3])}
          />
          <ImageCard
            image={teaserImages[4]}
            className="sm:col-span-4"
            aspectClass="aspect-[4/3] sm:aspect-[1/1]"
            delay={0.24}
            onClick={() => setLightboxIndex(teaserIndices[4])}
          />

          {/* Row 3: Closing panoramic — calm landing */}
          <ImageCard
            image={teaserImages[5]}
            className="sm:col-span-12"
            aspectClass="aspect-[16/9] sm:aspect-[21/8]"
            delay={0.28}
            onClick={() => setLightboxIndex(teaserIndices[5])}
          />
        </div>

        {/* Soft CTA — natural continuation */}
        <AnimatedSection className="text-center mt-16 sm:mt-24" gentle delay={0.3}>
          <Link
            to="/sfeerbeelden"
            className="inline-flex items-center gap-2.5 text-[12px] font-semibold text-accent/55 font-body uppercase tracking-[0.14em] group hover:text-accent transition-colors duration-[0.6s] ease-[cubic-bezier(0.22,0.68,0.35,1)]"
          >
            Bekijk alle sfeerbeelden
            <ArrowRight className="h-3.5 w-3.5 transition-all duration-[0.6s] ease-[cubic-bezier(0.22,0.68,0.35,1)] group-hover:translate-x-1.5" />
          </Link>
        </AnimatedSection>
      </div>

      <GalleryLightbox
        images={galleryImages}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
      />
    </section>
  );
};

export default GalleryTeaser;
